import type { ToolSeoContent } from "@/types/tools";

function localToolContent(content: ToolSeoContent): ToolSeoContent {
  return {
    privacy:
      "This tool runs in your browser. Inputs are processed on your device and are not uploaded to LocalTools.",
    ...content,
  };
}

function serverAssistedContent(content: ToolSeoContent): ToolSeoContent {
  return {
    privacy:
      "This tool uses a server request for public URL checks. Enter public HTTP or HTTPS URLs only; private, local, reserved, and credentialed hosts are blocked.",
    ...content,
  };
}

const privacyWorkflow = {
  title: "How LocalTools handles local and server-assisted tools",
  href: "/privacy",
  description:
    "Understand which tools stay in the browser and which public URL checks need an API request.",
};

const jsonFormatterContent: ToolSeoContent = {
  h1: "Local JSON Formatter and Validator",
  intro:
    "Format, validate, and pretty-print JSON directly in your browser. It is useful for API responses, logs, webhook payloads, and sensitive JSON you do not want to upload.",
  privacy:
    "JSON is parsed and formatted in the browser runtime. The pasted payload is not sent to a LocalTools server by this tool.",
  examples: [
    {
      title: "Format a compact API response",
      input: `{"user":{"id":123,"name":"Ada"},"active":true}`,
      output: `{
  "user": {
    "id": 123,
    "name": "Ada"
  },
  "active": true
}`,
      explanation:
        "Compact API responses and logs become easier to scan when nested objects and arrays are indented.",
    },
  ],
  useCases: [
    "Debug API responses without uploading customer data.",
    "Check whether a JSON payload is valid before using it in code or docs.",
    "Pretty-print compact JSON from logs, webhooks, browser storage, or copied network responses.",
    "Format JSON before adding it to tickets, documentation, or support notes.",
  ],
  howItWorks: [
    "The tool parses the text with the browser's JavaScript runtime.",
    "Valid JSON is serialized again with readable indentation.",
    "Invalid JSON returns a syntax error instead of formatted output.",
    "No request is made with the pasted JSON for this browser-local tool.",
  ],
  commonMistakes: [
    "Trailing commas are not valid JSON.",
    "Single quotes are not valid JSON string delimiters.",
    "Comments are not valid in standard JSON.",
    "Unescaped line breaks inside strings break JSON parsing.",
  ],
  relatedGuides: [
    {
      title: "Format JSON without uploading it",
      href: "/guides/format-json-without-uploading",
      description:
        "A practical workflow for inspecting API responses, logs, and webhook payloads locally.",
    },
  ],
  faqs: [
    {
      question: "Is my JSON uploaded to a server?",
      answer:
        "No. The JSON Formatter runs in your browser, so pasted JSON stays on your device.",
    },
    {
      question: "Can I format sensitive JSON here?",
      answer:
        "The tool is designed for local formatting. You should still avoid pasting secrets into browser extensions or pages you do not trust.",
    },
    {
      question: "Does this validate JSON syntax?",
      answer:
        "Yes. Invalid JSON returns a parsing error instead of formatted output.",
    },
    {
      question: "Why does a trailing comma break JSON?",
      answer:
        "Standard JSON does not allow trailing commas after the last object property or array item.",
    },
  ],
};

const jwtDecoderContent: ToolSeoContent = {
  h1: "Local JWT Decoder",
  intro:
    "Decode JWT headers and payloads locally without uploading the token. Use it to inspect claims, expiration timestamps, issuers, audiences, and debugging context.",
  privacy:
    "JWT decoding runs in the browser. The token is split and Base64URL-decoded locally; this tool does not upload the token or verify signatures.",
  examples: [
    {
      title: "Inspect an expiration claim",
      input:
        "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjMiLCJleHAiOjE3MzU2ODk2MDB9.signature",
      output: `{
  "sub": "123",
  "exp": 1735689600
}`,
      explanation:
        "The decoded payload lets you inspect common claims such as sub, exp, iat, nbf, iss, and aud.",
    },
  ],
  useCases: [
    "Debug an API request that may be failing because a token expired.",
    "Inspect OAuth or OpenID Connect claims without sending the token to a third-party decoder.",
    "Check the header algorithm and token type before investigating signature verification elsewhere.",
    "Copy decoded claims into a bug report without exposing the original token.",
  ],
  howItWorks: [
    "The token is split into header, payload, and signature segments.",
    "Header and payload are decoded from Base64URL in the browser.",
    "Decoded JSON is formatted for inspection.",
    "The signature segment is shown but not cryptographically verified.",
  ],
  commonMistakes: [
    "Decoded does not mean trusted. Signature verification is still required before accepting a token.",
    "JWT payloads are usually encoded, not encrypted, so anyone with the token can read the claims.",
    "The exp claim is a Unix timestamp in seconds, not milliseconds.",
    "Production access tokens should still be treated like secrets even when a decoder runs locally.",
  ],
  relatedGuides: [
    {
      title: "Decode a JWT locally",
      href: "/guides/decode-jwt-locally",
      description:
        "How to inspect JWT headers, payloads, and claims without uploading the token.",
    },
    {
      title: "Check JWT expiration",
      href: "/guides/check-jwt-expiration",
      description:
        "Use exp, iat, and nbf claims to debug expired or not-yet-valid tokens.",
    },
  ],
  faqs: [
    {
      question: "Does this verify JWT signatures?",
      answer:
        "No. It decodes the header and payload locally, but cryptographic signature verification must be done with the correct key and validation rules.",
    },
    {
      question: "Can anyone decode a JWT?",
      answer:
        "Yes, unless the token is encrypted as a JWE. Standard JWT payloads are encoded, not encrypted.",
    },
    {
      question: "What does exp mean?",
      answer:
        "exp is the expiration time claim. It is usually a Unix timestamp in seconds.",
    },
    {
      question: "Should I paste production tokens into online tools?",
      answer:
        "Avoid sharing production tokens. This decoder runs locally, but tokens should still be handled as sensitive credentials.",
    },
  ],
};

const base64Content: ToolSeoContent = {
  h1: "Local Base64 Encode and Decode",
  intro:
    "Encode Unicode text to Base64 or decode Base64 back to readable text in your browser, without sending the input to a server.",
  privacy:
    "Base64 conversion runs locally in the browser. Text is encoded or decoded on your device.",
  examples: [
    {
      title: "Decode a Base64 string",
      input: "SGVsbG8sIExvY2FsVG9vbHM=",
      output: "Hello, LocalTools",
      explanation:
        "Base64 is common in API payloads, Basic Auth examples, data URLs, and JWT segments.",
    },
  ],
  useCases: [
    "Decode Base64 snippets from API payloads, logs, or copied headers.",
    "Encode small text values for tests, examples, or documentation.",
    "Inspect JWT header or payload segments before formatting the decoded JSON.",
    "Convert Unicode text with browser-safe handling.",
  ],
  howItWorks: [
    "Input text is converted with browser APIs and Unicode-safe handling.",
    "Decode mode validates the Base64 input and returns readable text.",
    "Encode mode converts the current input into Base64.",
    "No Base64 input is uploaded by this browser-local tool.",
  ],
  commonMistakes: [
    "Base64 is encoding, not encryption.",
    "JWT uses Base64URL, which replaces some characters and may omit padding.",
    "Binary files and very large payloads may need a dedicated file workflow.",
  ],
  faqs: [
    {
      question: "Is Base64 secure?",
      answer:
        "No. Base64 only encodes data into a text-safe representation. Anyone can decode it.",
    },
    {
      question: "Is my Base64 text uploaded?",
      answer:
        "No. This Base64 tool runs locally in the browser.",
    },
    {
      question: "What is the difference between Base64 and Base64URL?",
      answer:
        "Base64URL is a URL-safe variant often used in JWTs. It replaces characters such as plus and slash and may omit padding.",
    },
  ],
};

const securityHeadersContent: ToolSeoContent = {
  h1: "Security Headers Auditor and CSP Checker",
  intro:
    "Check a public URL for CSP, HSTS, X-Frame-Options, Referrer-Policy, Permissions-Policy, CORS, and cross-origin isolation headers.",
  privacy:
    "This is a server-assisted public URL check. LocalTools sends only the public URL to its API, blocks private and reserved hosts, and reads response headers from the public site.",
  useCases: [
    "Audit a production or staging public URL before launch.",
    "Check whether a site has a baseline CSP, HSTS, clickjacking, and MIME-sniffing posture.",
    "Create a copyable remediation report for developers or clients.",
    "Compare security header changes after a framework, CDN, or proxy update.",
  ],
  howItWorks: [
    "The API fetches the public URL and follows safe public redirects.",
    "Private, local, reserved, and credentialed URLs are blocked before the request.",
    "Response headers are graded against a practical hardening checklist.",
    "The report explains missing, weak, deprecated, or overly broad headers.",
  ],
  commonMistakes: [
    "A report-only CSP does not enforce browser restrictions.",
    "HSTS without includeSubDomains may leave subdomains outside the policy.",
    "X-Frame-Options does not replace a well-scoped CSP frame-ancestors directive.",
    "CORS wildcard policies are often too broad for credentialed endpoints.",
  ],
  platformFixes: [
    {
      title: "Nginx baseline security headers",
      description:
        "Add headers at the server or location level, then re-run the auditor against the public URL.",
      snippet: `add_header Strict-Transport-Security "max-age=31536000; includeSubDomains" always;
add_header X-Content-Type-Options "nosniff" always;
add_header Referrer-Policy "strict-origin-when-cross-origin" always;
add_header Permissions-Policy "camera=(), microphone=(), geolocation=()" always;
add_header Content-Security-Policy "default-src 'self'; object-src 'none'; base-uri 'self'; frame-ancestors 'none'" always;`,
    },
    {
      title: "Caddy header block",
      description:
        "Use a site-level header block for a simple static or reverse-proxied app.",
      snippet: `header {
  Strict-Transport-Security "max-age=31536000; includeSubDomains"
  X-Content-Type-Options "nosniff"
  Referrer-Policy "strict-origin-when-cross-origin"
  Permissions-Policy "camera=(), microphone=(), geolocation=()"
  Content-Security-Policy "default-src 'self'; object-src 'none'; base-uri 'self'; frame-ancestors 'none'"
}`,
    },
    {
      title: "Express with Helmet",
      description:
        "Helmet gives Express apps a maintained starting point; tune CSP directives for your assets and integrations.",
      snippet: `import helmet from "helmet";

app.use(
  helmet({
    contentSecurityPolicy: {
      directives: {
        defaultSrc: ["'self'"],
        objectSrc: ["'none'"],
        baseUri: ["'self'"],
        frameAncestors: ["'none'"],
      },
    },
    hsts: { maxAge: 31536000, includeSubDomains: true },
    referrerPolicy: { policy: "strict-origin-when-cross-origin" },
  }),
);`,
    },
    {
      title: "Spring Security headers",
      description:
        "Use Spring Security header writers, then add an app-specific CSP that matches your frontend.",
      snippet: `http.headers((headers) -> headers
  .httpStrictTransportSecurity((hsts) -> hsts
    .includeSubDomains(true)
    .maxAgeInSeconds(31536000))
  .contentTypeOptions(Customizer.withDefaults())
  .referrerPolicy((policy) -> policy
    .policy(ReferrerPolicyHeaderWriter.ReferrerPolicy.STRICT_ORIGIN_WHEN_CROSS_ORIGIN))
  .contentSecurityPolicy((csp) -> csp
    .policyDirectives("default-src 'self'; object-src 'none'; base-uri 'self'; frame-ancestors 'none'")));`,
    },
    {
      title: "Go net/http middleware",
      description:
        "Set headers before calling the next handler so every response path gets the same baseline.",
      snippet: `func securityHeaders(next http.Handler) http.Handler {
  return http.HandlerFunc(func(w http.ResponseWriter, r *http.Request) {
    w.Header().Set("Strict-Transport-Security", "max-age=31536000; includeSubDomains")
    w.Header().Set("X-Content-Type-Options", "nosniff")
    w.Header().Set("Referrer-Policy", "strict-origin-when-cross-origin")
    w.Header().Set("Permissions-Policy", "camera=(), microphone=(), geolocation=()")
    w.Header().Set("Content-Security-Policy", "default-src 'self'; object-src 'none'; base-uri 'self'; frame-ancestors 'none'")
    next.ServeHTTP(w, r)
  })
}`,
    },
  ],
  relatedGuides: [
    {
      title: "Audit security headers before launch",
      href: "/guides/audit-security-headers-before-launch",
      description:
        "A launch checklist for CSP, HSTS, clickjacking, CORS, and privacy headers.",
    },
  ],
};

const redirectChainContent: ToolSeoContent = {
  h1: "Redirect Tracker",
  intro:
    "Use the Redirect Tracker to trace single or batch public URL redirects hop by hop with status codes, Location targets, URL deltas, headers, final URL, timing, and SEO cleanup signals.",
  privacy:
    "This is a server-assisted public URL check. LocalTools sends only the public URL to its API and blocks local, private, reserved, and credentialed URLs before following redirects.",
  examples: [
    {
      title: "Track a URL redirect from HTTP to HTTPS",
      input: "http://example.com",
      output: "Final URL, status code, redirect count, timing, and hop-by-hop Location headers",
      explanation:
        "A redirect tracker shows whether the first public URL reaches the expected canonical destination directly or takes extra hops.",
    },
    {
      title: "Batch-check migration redirects",
      input: `https://old.example.com/pricing
https://old.example.com/docs
https://old.example.com/contact`,
      output: "A comparison table with grade, redirect count, final URL, timing, and top cleanup issue for each URL",
      explanation:
        "Batch mode helps SEO and engineering teams spot redirect chains, loops, dropped query strings, and broken final URLs before a migration goes live.",
    },
  ],
  useCases: [
    "Use a redirect tracker to verify where a short link, old URL, or campaign URL actually lands.",
    "Verify a URL migration before search engines recrawl important pages.",
    "Run a batch of public URLs after changing CDN, CMS, or framework redirect rules.",
    "Find chains with too many redirect hops, temporary redirects, or final status errors.",
    "Check whether query parameters survive a redirect path.",
    "Confirm that HTTP URLs end on the expected HTTPS canonical URL.",
  ],
  howItWorks: [
    "The redirect tracker API requests the public URL and follows redirects manually.",
    "Each hop records status code, Location header, resolved destination, URL changes, headers, cache signals, cookies, and timing.",
    "Redirected destinations are checked again before following.",
    "The report flags loops, private redirects, HTTPS downgrades, cached temporary redirects, split canonicalization, slow chains, and SEO risks.",
  ],
  commonMistakes: [
    "A long chain can add latency and waste crawler time.",
    "Temporary redirects on permanent migrations can send weaker canonical signals.",
    "Dropping query parameters can break campaign tracking or application state.",
    "Splitting HTTP-to-HTTPS, www, path, and slash cleanup into separate rules creates avoidable hops.",
    "A redirect chain that ends in a 404 or 5xx still needs fixing.",
  ],
  platformFixes: [
    {
      title: "Cloudflare single-hop redirect rule",
      description:
        "Point the old pattern directly to the final canonical URL instead of chaining page rules.",
      snippet: `If incoming requests match:
  Hostname equals old.example.com
Then:
  Static redirect to https://www.example.com/$1
  Status code: 301
  Preserve query string: enabled`,
    },
    {
      title: "Vercel redirects",
      description:
        "Place redirects in configuration so old URLs resolve directly to the final destination.",
      snippet: `{
  "redirects": [
    {
      "source": "/old/:path*",
      "destination": "/new/:path*",
      "permanent": true
    }
  ]
}`,
    },
    {
      title: "Netlify redirects file",
      description:
        "Use a forced permanent redirect only when you are sure it should override existing files.",
      snippet: `/old/*  /new/:splat  301
http://example.com/*  https://www.example.com/:splat  301!`,
    },
    {
      title: "Apache RewriteRule",
      description:
        "Collapse protocol, host, and path changes into one redirect where possible.",
      snippet: `RewriteEngine On
RewriteCond %{HTTP_HOST} ^old\\.example\\.com$ [NC]
RewriteRule ^(.*)$ https://www.example.com/$1 [R=301,L,QSA]`,
    },
    {
      title: "Nginx canonical redirect",
      description:
        "Prefer an exact server block redirect over proxying through the old application first.",
      snippet: `server {
  listen 80;
  server_name old.example.com;
  return 301 https://www.example.com$request_uri;
}`,
    },
  ],
  relatedGuides: [
    {
      title: "Use a redirect tracker for SEO checks",
      href: "/guides/check-redirect-chain-for-seo",
      description:
        "How to inspect 301, 302, 307, and 308 redirects before a migration, cleanup, or canonical URL change.",
    },
  ],
  faqs: [
    {
      question: "What is a redirect tracker?",
      answer:
        "A redirect tracker follows a public URL from the first request through every HTTP redirect until it reaches the final URL, then reports each hop, status code, Location target, timing, and cleanup issue.",
    },
    {
      question: "Can the Redirect Tracker check 301 and 302 redirects?",
      answer:
        "Yes. It tracks 301, 302, 303, 307, 308, and other 3xx responses, then labels permanent and temporary redirects so you can audit migration and canonical rules.",
    },
    {
      question: "Can I use the Redirect Tracker for batch URL checks?",
      answer:
        "Yes. Batch mode accepts multiple public URLs and returns a comparison table with grades, redirect counts, final URLs, timing, and the top issue for each trace.",
    },
    {
      question: "Does the Redirect Tracker follow private or local URLs?",
      answer:
        "No. Local, private, reserved, and credentialed hosts are blocked before the request, and every redirected destination is checked before another hop is followed.",
    },
  ],
};

const jsonMinifierContent = localToolContent({
  h1: "Local JSON Minifier",
  intro:
    "Validate JSON and remove unnecessary whitespace in your browser when you need compact payloads for tests, docs, or transport examples.",
  examples: [
    {
      title: "Minify formatted JSON",
      input: `{
  "plan": "pro",
  "active": true
}`,
      output: `{"plan":"pro","active":true}`,
      explanation:
        "Minified JSON is useful when an API example, fixture, or embedded configuration needs to stay compact.",
    },
  ],
  useCases: [
    "Shrink formatted JSON for documentation snippets.",
    "Validate a payload before adding it to a test fixture.",
    "Create compact request examples without uploading private data.",
  ],
  howItWorks: [
    "The input is parsed as standard JSON in the browser.",
    "Valid JSON is serialized without indentation or extra whitespace.",
    "Invalid JSON returns a parsing error so the source can be corrected.",
  ],
  commonMistakes: [
    "Minification does not encrypt or hide sensitive values.",
    "JSON with comments, trailing commas, or single-quoted strings is invalid.",
    "Whitespace inside string values is preserved because it is part of the data.",
  ],
  relatedGuides: [
    {
      title: "Format JSON without uploading it",
      href: "/guides/format-json-without-uploading",
      description:
        "Use the formatter first when you need to inspect a payload before minifying it.",
    },
  ],
  faqs: [
    {
      question: "Does JSON minification change the data?",
      answer:
        "No. It removes whitespace outside values. The parsed object should represent the same data.",
    },
    {
      question: "Is minified JSON secure?",
      answer:
        "No. Minified JSON is still plain text and can be read by anyone who has it.",
    },
  ],
});

const yamlToJsonContent = localToolContent({
  h1: "Local YAML to JSON Converter",
  intro:
    "Convert YAML documents into formatted JSON locally for configs, examples, and data cleanup without uploading the source text.",
  examples: [
    {
      title: "Convert a small YAML object",
      input: `name: LocalTools
tags:
  - privacy
  - developer`,
      output: `{
  "name": "LocalTools",
  "tags": [
    "privacy",
    "developer"
  ]
}`,
      explanation:
        "YAML configuration can be converted into JSON for APIs, test fixtures, or tooling that does not accept YAML.",
    },
  ],
  useCases: [
    "Convert configuration snippets into JSON for docs or API tests.",
    "Inspect YAML structures with JSON indentation.",
    "Normalize small YAML examples before pasting them into code.",
  ],
  howItWorks: [
    "The YAML input is parsed with the browser-loaded parser.",
    "The parsed value is serialized as formatted JSON.",
    "The conversion runs locally after the page loads.",
  ],
  commonMistakes: [
    "Indentation changes YAML meaning.",
    "Tabs are a common cause of YAML parsing failures.",
    "Advanced YAML features such as anchors can surprise downstream JSON consumers.",
  ],
  relatedGuides: [privacyWorkflow],
  faqs: [
    {
      question: "Can every YAML file become JSON?",
      answer:
        "Most ordinary maps, arrays, strings, numbers, and booleans convert cleanly. YAML features without a JSON equivalent may need cleanup.",
    },
    {
      question: "Is the YAML uploaded?",
      answer:
        "No. The conversion runs in your browser.",
    },
  ],
});

const htmlEscapeContent = localToolContent({
  h1: "Local HTML Escape and Unescape",
  intro:
    "Escape HTML special characters or decode common entities locally when preparing snippets, docs, templates, or copied markup.",
  examples: [
    {
      title: "Escape a script-like snippet",
      input: `<strong>Hello</strong> & "welcome"`,
      output: `&lt;strong&gt;Hello&lt;/strong&gt; &amp; &quot;welcome&quot;`,
      explanation:
        "Escaping lets markup appear as text instead of being interpreted as HTML.",
    },
  ],
  useCases: [
    "Show code snippets safely in HTML documentation.",
    "Decode copied entity-heavy text into readable characters.",
    "Prepare small strings for templates or CMS fields.",
  ],
  howItWorks: [
    "Escape mode replaces special characters with HTML entities.",
    "Unescape mode decodes common HTML entities back to text.",
    "All conversion happens in the browser.",
  ],
  commonMistakes: [
    "HTML escaping is context-specific and does not replace output encoding in an app.",
    "Escaping HTML is different from URL encoding.",
    "Double-escaping can leave visible entities in rendered content.",
  ],
  relatedGuides: [privacyWorkflow],
  faqs: [
    {
      question: "Does this sanitize unsafe HTML?",
      answer:
        "No. It escapes or unescapes text. Application security still needs context-aware output encoding and sanitization.",
    },
    {
      question: "What characters are usually escaped?",
      answer:
        "Common HTML escaping includes ampersand, less-than, greater-than, double quote, and single quote.",
    },
  ],
});

const uuidGeneratorContent = localToolContent({
  h1: "Local UUID v4 Generator",
  intro:
    "Generate one or many v4 UUIDs in your browser for tests, fixtures, records, and temporary identifiers.",
  examples: [
    {
      title: "Generate IDs for seed data",
      output: `6d2f4f9e-8d25-4fc8-a3c4-2b7b5c8e9c10
55d2d98a-8c30-4a3b-a02a-f49755f3a327`,
      explanation:
        "UUIDs are convenient when test data needs stable-looking unique identifiers.",
    },
  ],
  useCases: [
    "Create IDs for local test fixtures.",
    "Generate sample identifiers for docs or mock API responses.",
    "Batch-generate values for temporary records.",
  ],
  howItWorks: [
    "The browser crypto API generates random bytes.",
    "Bytes are formatted as RFC 4122-style version 4 UUIDs.",
    "Generated values stay on your device unless you copy them elsewhere.",
  ],
  commonMistakes: [
    "UUIDs are identifiers, not secrets.",
    "Random UUIDs are not naturally sortable by creation time.",
    "Do not use UUID generation as an authorization mechanism.",
  ],
  relatedGuides: [privacyWorkflow],
  faqs: [
    {
      question: "Are UUID v4 values guaranteed unique?",
      answer:
        "They are random with an extremely low collision probability, but uniqueness is still probabilistic.",
    },
    {
      question: "Can I use a UUID as a password?",
      answer:
        "No. UUIDs are identifiers. Use the Password Generator for passwords or tokens.",
    },
  ],
});

const hashGeneratorContent = localToolContent({
  h1: "Local SHA Hash Generator",
  intro:
    "Generate SHA-1, SHA-256, and SHA-512 hashes locally with Web Crypto for checksums, comparisons, and repeatable test values.",
  examples: [
    {
      title: "Hash a short string",
      input: "LocalTools",
      output: "SHA-256 digest output",
      explanation:
        "Hashes are useful for comparing values without storing the original text in a fixture or note.",
    },
  ],
  useCases: [
    "Create checksums for small text snippets.",
    "Compare whether two copied values are the same.",
    "Generate sample digests for documentation or tests.",
  ],
  howItWorks: [
    "Text is encoded in the browser.",
    "Web Crypto computes the selected SHA digest.",
    "The digest is displayed as a hex string.",
  ],
  commonMistakes: [
    "A hash is not encryption and cannot be decrypted.",
    "Fast hashes are not enough for password storage.",
    "Changing a single character changes the digest completely.",
  ],
  relatedGuides: [privacyWorkflow],
  faqs: [
    {
      question: "Can this hash files?",
      answer:
        "This tool is focused on text input. Large file hashing needs a dedicated file workflow.",
    },
    {
      question: "Which SHA option should I use?",
      answer:
        "SHA-256 is a common default for checksums and examples. Password storage needs a password hashing function, not a plain SHA digest.",
    },
  ],
});

const passwordGeneratorContent = localToolContent({
  h1: "Local Password Generator",
  intro:
    "Create strong random passwords in your browser with configurable length and character rules.",
  examples: [
    {
      title: "Generate a long random password",
      output: "Example output: q9Vx7pR4nL2sT8mK6zW3",
      explanation:
        "Long random passwords are easier to make safely with a generator than by inventing patterns manually.",
    },
  ],
  useCases: [
    "Create throwaway development credentials.",
    "Generate strong passwords before storing them in a password manager.",
    "Create random shared-secret examples for docs or tests.",
  ],
  howItWorks: [
    "The browser crypto API supplies random values.",
    "Your selected character rules are applied locally.",
    "Generated passwords are not sent to a server.",
  ],
  commonMistakes: [
    "Short passwords with many character classes can still be weaker than long passphrases.",
    "Do not reuse generated passwords across accounts.",
    "Store real credentials in a password manager after generation.",
  ],
  relatedGuides: [privacyWorkflow],
  faqs: [
    {
      question: "Are generated passwords uploaded?",
      answer:
        "No. Password generation runs in your browser.",
    },
    {
      question: "Should I memorize generated passwords?",
      answer:
        "Usually no. Store unique passwords in a trusted password manager.",
    },
  ],
});

const timestampConverterContent = localToolContent({
  h1: "Local Unix Timestamp Converter",
  intro:
    "Convert Unix timestamps, ISO strings, and local dates in your browser when debugging logs, JWT claims, schedulers, and API events.",
  examples: [
    {
      title: "Convert a JWT exp timestamp",
      input: "1735689600",
      output: "2025-01-01T00:00:00.000Z",
      explanation:
        "JWT exp, iat, and nbf claims are usually Unix timestamps in seconds.",
    },
  ],
  useCases: [
    "Debug JWT expiration and issued-at claims.",
    "Read timestamps from logs or webhook payloads.",
    "Compare UTC and local times during incident response.",
  ],
  howItWorks: [
    "The tool parses Unix timestamps, ISO strings, or date input.",
    "It renders equivalent UTC and local time values.",
    "All conversion happens in the browser.",
  ],
  commonMistakes: [
    "Unix seconds and JavaScript milliseconds differ by a factor of 1000.",
    "Timezone display does not change the actual instant in time.",
    "Some logs omit timezone information, which can cause ambiguity.",
  ],
  relatedGuides: [
    {
      title: "Check JWT expiration",
      href: "/guides/check-jwt-expiration",
      description:
        "Use timestamp conversion to understand exp, iat, and nbf claims.",
    },
  ],
  faqs: [
    {
      question: "Are JWT timestamps seconds or milliseconds?",
      answer:
        "JWT NumericDate values such as exp are normally Unix timestamps in seconds.",
    },
    {
      question: "Why does local time differ from UTC?",
      answer:
        "They are two displays of the same instant using different time zones.",
    },
  ],
});

const urlEncoderContent = localToolContent({
  h1: "Local URL Encoder and Decoder",
  intro:
    "Percent-encode URL components or decode encoded URL text locally for query strings, redirects, and API parameters.",
  examples: [
    {
      title: "Encode a query value",
      input: "spring sale + coupons",
      output: "spring%20sale%20%2B%20coupons",
      explanation:
        "Encoding keeps spaces, plus signs, and reserved characters from being misread inside URLs.",
    },
  ],
  useCases: [
    "Encode query parameter values before sharing a URL.",
    "Decode copied URLs to inspect tracking or redirect parameters.",
    "Prepare values for API examples without uploading them.",
  ],
  howItWorks: [
    "Encode mode applies percent encoding for URL components.",
    "Decode mode turns percent-encoded text back into readable characters.",
    "The transformation runs locally in the browser.",
  ],
  commonMistakes: [
    "Encoding a full URL and encoding one query value are different tasks.",
    "A plus sign can mean a literal plus or a space depending on context.",
    "Double-encoding creates hard-to-read URLs with percent signs encoded again.",
  ],
  relatedGuides: [
    {
      title: "Check redirect chains for SEO",
      href: "/guides/check-redirect-chain-for-seo",
      description:
        "Encoded query parameters often matter when validating redirects and migrations.",
    },
  ],
  faqs: [
    {
      question: "Should I encode an entire URL?",
      answer:
        "Usually no. Encode individual parameter values unless a specific API expects a full encoded URL.",
    },
    {
      question: "Is URL encoding private here?",
      answer:
        "Yes. This tool encodes and decodes text in your browser.",
    },
  ],
});

const colorConverterContent = localToolContent({
  h1: "Local CSS Color Converter",
  intro:
    "Convert HEX, RGB, RGBA, and HSL-style color values locally when cleaning up CSS, design tokens, and UI notes.",
  examples: [
    {
      title: "Convert HEX to RGB/HSL",
      input: "#0ea5e9",
      output: "rgb(14, 165, 233)",
      explanation:
        "Converting formats helps match the syntax expected by CSS variables, design tools, or component props.",
    },
  ],
  useCases: [
    "Translate colors between CSS formats.",
    "Normalize values before adding them to design tokens.",
    "Inspect opacity and channel values while tweaking UI.",
  ],
  howItWorks: [
    "The input color is parsed in the browser.",
    "Equivalent color formats are calculated from the parsed channels.",
    "No color values are uploaded.",
  ],
  commonMistakes: [
    "Three-digit HEX expands each digit, so #0af is not the same as #00aaff in every mental model.",
    "RGBA alpha is opacity, not brightness.",
    "Different color spaces can represent similar-looking but not identical values.",
  ],
  relatedGuides: [privacyWorkflow],
  faqs: [
    {
      question: "Does converting a color change how it looks?",
      answer:
        "Equivalent conversions should preserve appearance, though rounding can slightly change displayed values.",
    },
    {
      question: "Can I convert named CSS colors?",
      answer:
        "Use explicit HEX, RGB, or HSL values for the most predictable result.",
    },
  ],
});

const csvToMarkdownContent = localToolContent({
  h1: "Local CSV to Markdown Table Converter",
  intro:
    "Turn simple CSV rows into a Markdown table locally for README files, issues, docs, and release notes.",
  examples: [
    {
      title: "Convert CSV rows to a table",
      input: `Name,Status
JSON Formatter,Available
JWT Decoder,Available`,
      output: `| Name | Status |
| --- | --- |
| JSON Formatter | Available |
| JWT Decoder | Available |`,
      explanation:
        "Markdown tables are easier to read in GitHub issues, docs, and changelogs.",
    },
  ],
  useCases: [
    "Convert small spreadsheet exports into documentation tables.",
    "Prepare issue comments or README sections.",
    "Clean up release notes without uploading the CSV.",
  ],
  howItWorks: [
    "The tool splits CSV rows and cells in the browser.",
    "The first row is treated as the table header.",
    "Markdown table syntax is generated from the parsed rows.",
  ],
  commonMistakes: [
    "Complex CSV with quoted commas may need a full spreadsheet parser.",
    "Markdown tables need consistent column counts.",
    "Very wide tables are hard to read on mobile.",
  ],
  relatedGuides: [privacyWorkflow],
  faqs: [
    {
      question: "Is this a full CSV parser?",
      answer:
        "It is intended for simple CSV-to-Markdown cleanup. Complex exports with quoted commas or embedded newlines may need spreadsheet tooling.",
    },
    {
      question: "Is my CSV uploaded?",
      answer:
        "No. Conversion runs in your browser.",
    },
  ],
});

const urlParserContent = localToolContent({
  h1: "Local URL Parser",
  intro:
    "Break a URL into protocol, hostname, path, query parameters, and hash locally so you can debug links, redirects, and campaign URLs.",
  examples: [
    {
      title: "Inspect query parameters",
      input: "https://example.com/pricing?utm_source=newsletter#plans",
      output: "protocol: https, host: example.com, path: /pricing, query: utm_source=newsletter",
      explanation:
        "Parsing reveals which part of a URL controls routing, tracking, and fragments.",
    },
  ],
  useCases: [
    "Debug tracking URLs and campaign links.",
    "Inspect redirect destinations before sharing them.",
    "Separate query parameters from path and hash values.",
  ],
  howItWorks: [
    "The browser URL parser reads the provided URL.",
    "The parsed pieces are displayed in separate fields.",
    "Input is processed locally.",
  ],
  commonMistakes: [
    "A hash fragment is not sent to the server in normal HTTP requests.",
    "Relative URLs need a base URL to parse correctly.",
    "Query parameter order can matter for some applications.",
  ],
  relatedGuides: [
    {
      title: "Check redirect chains for SEO",
      href: "/guides/check-redirect-chain-for-seo",
      description:
        "Use URL parsing before and after redirect checks to confirm query handling.",
    },
  ],
  faqs: [
    {
      question: "Does parsing a URL request it?",
      answer:
        "No. This parser only reads the URL text locally and does not fetch the destination.",
    },
    {
      question: "What is the hash part of a URL?",
      answer:
        "The hash is the fragment after #. It is usually handled by the browser or client app.",
    },
  ],
});

const curlTranslatorContent = localToolContent({
  h1: "cURL Translator and Explainer",
  intro:
    "Paste a cURL command and translate it locally into structured request details, plain-English notes, and code snippets for Fetch, Axios, Python requests, HTTPie, and cleaner cURL.",
  examples: [
    {
      title: "Translate a JSON API request",
      input: `curl 'https://api.example.com/users' -H 'Authorization: Bearer token' --json '{"name":"Ada"}'`,
      output: "POST request with JSON body, Authorization header, generated Fetch and Python snippets",
      explanation:
        "The translator separates URL, headers, auth, body, and options so an API request is easier to review before copying into code.",
    },
  ],
  useCases: [
    "Convert browser DevTools Copy as cURL output into application code.",
    "Explain an API request before sharing it with a teammate.",
    "Spot risky flags such as disabled TLS verification or duplicate headers.",
    "Redact likely secrets before copying generated examples into docs or tickets.",
  ],
  howItWorks: [
    "The command is tokenized and parsed in the browser.",
    "Common cURL flags are mapped to method, URL, query, headers, cookies, auth, body, and options.",
    "Generated snippets are built locally from the parsed request.",
    "The command is not executed and no request is sent.",
  ],
  commonMistakes: [
    "Shell variables are not resolved by a local parser.",
    "Local file uploads need manual replacement in generated snippets.",
    "Some PowerShell and Windows CMD quoting patterns may need small cleanup before parsing.",
    "Generated multipart browser code should usually let FormData set the Content-Type boundary.",
  ],
  relatedGuides: [privacyWorkflow],
  faqs: [
    {
      question: "Does this run the cURL request?",
      answer:
        "No. It only parses and translates the command locally in your browser.",
    },
    {
      question: "Are tokens and cookies uploaded?",
      answer:
        "No. Translation runs locally. The redaction toggle helps avoid copying secrets into generated output.",
    },
    {
      question: "Can it translate every cURL flag?",
      answer:
        "It handles common API request flags and calls out unsupported flags so you can review edge cases manually.",
    },
  ],
});

const textDiffContent = localToolContent({
  h1: "Local Text Diff",
  intro:
    "Compare two blocks of text line by line in your browser to spot documentation, config, or copy changes.",
  examples: [
    {
      title: "Compare two release note drafts",
      input: "Left: Format JSON locally\nRight: Format JSON without uploading",
      output: "Changed line highlighted in the diff",
      explanation:
        "A quick local diff helps review small text changes without uploading private notes.",
    },
  ],
  useCases: [
    "Compare configuration snippets.",
    "Review edits to docs, emails, or release notes.",
    "Spot accidental line changes in copied text.",
  ],
  howItWorks: [
    "Both text blocks are split into lines.",
    "Lines are compared in the browser.",
    "Differences are displayed without sending either side to a server.",
  ],
  commonMistakes: [
    "Whitespace-only changes can still be meaningful.",
    "Line-based diffs are less useful for heavily wrapped prose.",
    "A visual diff does not validate syntax.",
  ],
  relatedGuides: [privacyWorkflow],
  faqs: [
    {
      question: "Is either side of the diff uploaded?",
      answer:
        "No. The comparison runs locally in your browser.",
    },
    {
      question: "Can this diff files?",
      answer:
        "This tool compares pasted text blocks, not uploaded files.",
    },
  ],
});

const textStatisticsContent = localToolContent({
  h1: "Local Text Statistics",
  intro:
    "Count words, characters, lines, paragraphs, and reading time locally for docs, posts, snippets, and support replies.",
  examples: [
    {
      title: "Measure a short paragraph",
      input: "LocalTools keeps sensitive snippets in your browser.",
      output: "Words, characters, lines, paragraphs, and reading time",
      explanation:
        "Text metrics help keep titles, descriptions, and short-form content within practical limits.",
    },
  ],
  useCases: [
    "Check meta descriptions and social copy length.",
    "Estimate reading time for documentation snippets.",
    "Count lines or paragraphs in copied notes.",
  ],
  howItWorks: [
    "The browser counts visible characters, words, lines, and paragraphs.",
    "Reading time is estimated from word count.",
    "Text is not uploaded.",
  ],
  commonMistakes: [
    "Different platforms count emoji, whitespace, and line breaks differently.",
    "Reading time is an estimate, not an accessibility measure.",
    "Character count is not the same as byte size.",
  ],
  relatedGuides: [privacyWorkflow],
  faqs: [
    {
      question: "Does this match every platform character counter?",
      answer:
        "No. Platforms may count Unicode characters, whitespace, and line breaks differently.",
    },
    {
      question: "Is the text stored?",
      answer:
        "No server storage is used for this browser-local counter.",
    },
  ],
});

const slugGeneratorContent = localToolContent({
  h1: "Local Slug Generator",
  intro:
    "Create URL-safe slugs with hyphen or underscore separators locally for pages, posts, product names, and docs.",
  examples: [
    {
      title: "Create a clean page slug",
      input: "Local-first Developer Tools",
      output: "local-first-developer-tools",
      explanation:
        "Slugs make titles easier to use in URLs, docs, and static route names.",
    },
  ],
  useCases: [
    "Generate blog or docs URL slugs.",
    "Normalize product or category names for routes.",
    "Prepare file-safe identifiers from human-readable titles.",
  ],
  howItWorks: [
    "Input is normalized in the browser.",
    "Spaces and punctuation are converted to the chosen separator.",
    "Unsafe URL characters are removed or simplified.",
  ],
  commonMistakes: [
    "Changing a published slug can break existing links without redirects.",
    "Overly long slugs are harder to scan in search results.",
    "Slug generation does not check for duplicates in your CMS.",
  ],
  relatedGuides: [
    {
      title: "Check redirect chains for SEO",
      href: "/guides/check-redirect-chain-for-seo",
      description:
        "Use redirect checks when changing published slugs.",
    },
  ],
  faqs: [
    {
      question: "Should slugs use hyphens or underscores?",
      answer:
        "Hyphens are the common default for readable web URLs.",
    },
    {
      question: "Does this publish or reserve the slug?",
      answer:
        "No. It only generates text locally.",
    },
  ],
});

const caseConverterContent = localToolContent({
  h1: "Local Case Converter",
  intro:
    "Convert text into camelCase, PascalCase, snake_case, kebab-case, title case, and other common naming formats locally.",
  examples: [
    {
      title: "Convert a label to code-friendly names",
      input: "customer account id",
      output: "customerAccountId, CustomerAccountId, customer_account_id",
      explanation:
        "Case conversion is handy when moving between UI labels, database fields, CSS classes, and code identifiers.",
    },
  ],
  useCases: [
    "Convert API field names for examples.",
    "Create code identifiers from plain-language labels.",
    "Normalize headings or titles without uploading draft copy.",
  ],
  howItWorks: [
    "The browser tokenizes the input words.",
    "Words are recombined according to the selected case style.",
    "The converted text stays on your device.",
  ],
  commonMistakes: [
    "Acronyms may need manual cleanup after conversion.",
    "Case conversion does not validate language-specific naming rules.",
    "Changing case can change meaning for case-sensitive systems.",
  ],
  relatedGuides: [privacyWorkflow],
  faqs: [
    {
      question: "Can this preserve acronyms?",
      answer:
        "Some acronym cases may need manual adjustment after conversion.",
    },
    {
      question: "Is case conversion local?",
      answer:
        "Yes. It runs in your browser.",
    },
  ],
});

const utmBuilderContent = localToolContent({
  h1: "Local UTM Builder",
  intro:
    "Generate campaign URLs with validated UTM parameters locally before sharing links in email, ads, social posts, or partner campaigns.",
  examples: [
    {
      title: "Build a newsletter campaign URL",
      input: "https://example.com/pricing + source=newsletter + medium=email + campaign=spring",
      output: "https://example.com/pricing?utm_source=newsletter&utm_medium=email&utm_campaign=spring",
      explanation:
        "Consistent UTM names make analytics reports easier to group after launch.",
    },
  ],
  useCases: [
    "Create campaign links for email and social posts.",
    "Normalize UTM naming before handing links to a team.",
    "Inspect whether an existing URL already has query parameters.",
  ],
  howItWorks: [
    "The destination URL and UTM fields are combined in the browser.",
    "Parameter values are encoded safely for URLs.",
    "The generated URL is displayed for copying.",
  ],
  commonMistakes: [
    "Inconsistent source and medium names fragment analytics reports.",
    "Adding UTMs to internal navigation can pollute attribution.",
    "Unencoded spaces or symbols can break campaign URLs.",
  ],
  relatedGuides: [
    {
      title: "Check redirect chains for SEO",
      href: "/guides/check-redirect-chain-for-seo",
      description:
        "Confirm redirects preserve campaign parameters before sending traffic.",
    },
  ],
  faqs: [
    {
      question: "Does the UTM Builder shorten links?",
      answer:
        "No. It generates the tagged URL; shortening should be done in your link platform if needed.",
    },
    {
      question: "Are campaign URLs uploaded?",
      answer:
        "No. URL construction happens in your browser.",
    },
  ],
});

const metaTagGeneratorContent = localToolContent({
  h1: "Local Meta Tag Generator",
  intro:
    "Generate title, description, canonical, OpenGraph, and social meta tags locally for page templates and launch checklists.",
  examples: [
    {
      title: "Generate basic page metadata",
      input: "Title, description, canonical URL, and social image",
      output: `<title>LocalTools</title>
<meta name="description" content="Local-first developer tools">`,
      explanation:
        "A generated snippet helps developers copy consistent tags into a template or CMS field.",
    },
  ],
  useCases: [
    "Draft metadata for new landing pages.",
    "Create OpenGraph tags for social previews.",
    "Check title and description copy before implementation.",
  ],
  howItWorks: [
    "Inputs are assembled into HTML meta tags in the browser.",
    "Canonical and OpenGraph fields are included when provided.",
    "No page metadata draft is uploaded.",
  ],
  commonMistakes: [
    "Meta tags only help if they are present in the rendered HTML.",
    "Duplicate canonical URLs can confuse indexing signals.",
    "Social platforms may cache old OpenGraph data.",
  ],
  relatedGuides: [privacyWorkflow],
  faqs: [
    {
      question: "Does this add tags to my website?",
      answer:
        "No. It generates HTML for you to copy into your site or application.",
    },
    {
      question: "Should every page have a unique title and description?",
      answer:
        "Yes. Unique metadata helps people and search engines understand each page.",
    },
  ],
});

const openGraphPreviewContent = localToolContent({
  h1: "Local OpenGraph Preview",
  intro:
    "Preview social card copy and generate matching OpenGraph tags locally before publishing or sharing a page.",
  examples: [
    {
      title: "Preview a social share card",
      input: "Page title, description, URL, and image URL",
      output: "A social-style preview card plus matching meta tags",
      explanation:
        "Previewing the card helps catch cramped titles, vague descriptions, or missing images before launch.",
    },
  ],
  useCases: [
    "Draft social metadata for a launch page.",
    "Check whether title and description fit a card layout.",
    "Generate OpenGraph tags for static pages or templates.",
  ],
  howItWorks: [
    "The preview is rendered locally from the provided fields.",
    "Generated tags are assembled in the browser.",
    "The tool does not crawl or validate the remote page.",
  ],
  commonMistakes: [
    "A preview here does not clear platform caches.",
    "Remote images need to be publicly accessible for social crawlers.",
    "OpenGraph tags should match the live page content.",
  ],
  relatedGuides: [privacyWorkflow],
  faqs: [
    {
      question: "Does this fetch my website?",
      answer:
        "No. It previews the fields you enter locally.",
    },
    {
      question: "Why might a social platform show a different card?",
      answer:
        "Platforms cache metadata and may have their own image, size, and crawler rules.",
    },
  ],
});

const robotsGeneratorContent = localToolContent({
  h1: "Local robots.txt Generator",
  intro:
    "Create common robots.txt rules locally for crawl access, blocked paths, sitemap references, and launch checks.",
  examples: [
    {
      title: "Allow crawling and declare a sitemap",
      output: `User-agent: *
Allow: /

Sitemap: https://example.com/sitemap.xml`,
      explanation:
        "A simple robots file can make crawl permissions and sitemap discovery explicit.",
    },
  ],
  useCases: [
    "Draft a robots.txt file for a new site.",
    "Block staging-only or private-looking public paths from crawling.",
    "Add a sitemap reference before Search Console submission.",
  ],
  howItWorks: [
    "Selected rules are assembled into robots.txt syntax in the browser.",
    "The output is plain text for copying into your site.",
    "No site data is uploaded.",
  ],
  commonMistakes: [
    "robots.txt is public and should not list secret URLs.",
    "Disallow does not remove already indexed URLs by itself.",
    "Blocking CSS or JavaScript can make rendering harder for search engines.",
  ],
  relatedGuides: [privacyWorkflow],
  faqs: [
    {
      question: "Does robots.txt enforce security?",
      answer:
        "No. It is a crawler instruction, not access control.",
    },
    {
      question: "Where should robots.txt live?",
      answer:
        "It should be served from the site root, such as https://example.com/robots.txt.",
    },
  ],
});

const imageMetadataStripperContent = localToolContent({
  h1: "Local Image Metadata Stripper",
  intro:
    "Remove EXIF and editor metadata by re-encoding images locally before uploading screenshots, product photos, or support attachments.",
  examples: [
    {
      title: "Strip metadata from a photo",
      input: "Upload image in browser",
      output: "Re-encoded image without embedded EXIF metadata",
      explanation:
        "Re-encoding helps remove camera, editor, and location metadata before a file leaves your device.",
    },
  ],
  useCases: [
    "Remove EXIF metadata before sharing images publicly.",
    "Clean screenshots before attaching them to tickets.",
    "Create a fresh image export in a chosen format and quality.",
  ],
  howItWorks: [
    "The image is loaded into the browser.",
    "A canvas re-encodes the visible pixels into a new image file.",
    "The original file is not uploaded to LocalTools.",
  ],
  commonMistakes: [
    "Metadata stripping does not blur visible private information in the pixels.",
    "Re-encoding can change file size or image quality.",
    "Some file formats and browser decoders may behave differently.",
  ],
  relatedGuides: [privacyWorkflow],
  faqs: [
    {
      question: "Does this remove visible sensitive information?",
      answer:
        "No. It targets embedded metadata. You still need to crop or blur visible secrets manually.",
    },
    {
      question: "Is the image uploaded?",
      answer:
        "No. The image is processed in your browser.",
    },
  ],
});

const imageResizeContent = localToolContent({
  h1: "Local Image Resize",
  intro:
    "Resize images locally in your browser with canvas controls for dimensions, quality, and lightweight export workflows.",
  examples: [
    {
      title: "Resize a large screenshot",
      input: "2400 x 1600 screenshot",
      output: "1200 x 800 resized image",
      explanation:
        "Resizing locally is useful before uploading a screenshot to docs, support, or a CMS.",
    },
  ],
  useCases: [
    "Shrink large screenshots before sharing.",
    "Create smaller web-ready images without an upload step.",
    "Adjust dimensions for documentation or social assets.",
  ],
  howItWorks: [
    "The image is decoded by the browser.",
    "Canvas draws it at the selected dimensions.",
    "The result is exported locally in the selected format and quality.",
  ],
  commonMistakes: [
    "Upscaling cannot add real detail to a low-resolution source.",
    "Lower quality settings can introduce compression artifacts.",
    "Resizing does not automatically remove visible sensitive information.",
  ],
  relatedGuides: [privacyWorkflow],
  faqs: [
    {
      question: "Is the image uploaded for resizing?",
      answer:
        "No. Resizing happens locally in your browser.",
    },
    {
      question: "Will resizing remove metadata?",
      answer:
        "Canvas export often creates a fresh file, but use the metadata stripper when metadata removal is the goal.",
    },
  ],
});

const svgToImageContent = localToolContent({
  h1: "SVG to Image Converter",
  intro:
    "Convert SVG markup or SVG files into PNG, JPEG, or WebP images locally with preview, dimensions, and download controls.",
  examples: [
    {
      title: "Export an SVG logo as PNG",
      input: "SVG logo markup",
      output: "PNG image with selected dimensions",
      explanation:
        "Raster export is useful when a CMS, document, or social workflow needs a bitmap instead of vector markup.",
    },
  ],
  useCases: [
    "Convert SVG icons or logos to PNG before uploading.",
    "Create JPEG or WebP exports from vector artwork.",
    "Preview pasted SVG markup as a downloadable image.",
  ],
  howItWorks: [
    "The SVG is parsed in the browser.",
    "Optional CSS rules are embedded for SVGs that use class names for fills and strokes.",
    "External references are blocked before rendering.",
    "Canvas rasterizes the SVG at the selected dimensions and exports the chosen image format.",
  ],
  commonMistakes: [
    "SVGs copied without their matching CSS can render black because class names alone do not include colors.",
    "JPEG does not support transparency, so transparent areas use the selected background.",
    "Very large output dimensions can exceed browser canvas memory limits.",
  ],
  relatedGuides: [privacyWorkflow],
  faqs: [
    {
      question: "Is the SVG uploaded for conversion?",
      answer:
        "No. SVG conversion runs in your browser and blocks external references to keep the render local.",
    },
    {
      question: "Can I convert SVG to PNG?",
      answer:
        "Yes. The tool exports PNG, JPEG, and WebP from pasted SVG markup or an SVG file.",
    },
  ],
});

const pdfMergeContent = localToolContent({
  h1: "Local PDF Merge",
  intro:
    "Combine multiple PDF files into one browser-generated document without uploading the files to a server.",
  examples: [
    {
      title: "Merge several PDFs",
      input: "contract.pdf + appendix.pdf + receipt.pdf",
      output: "merged.pdf",
      explanation:
        "Local PDF merging is useful for lightweight document assembly when files should stay on your machine.",
    },
  ],
  useCases: [
    "Combine forms, receipts, or exports before sending them.",
    "Assemble documentation packets locally.",
    "Merge PDFs for personal workflows without account creation.",
  ],
  howItWorks: [
    "PDF files are loaded by the browser.",
    "Pages are copied into a new PDF document.",
    "The merged file is generated locally for download.",
  ],
  commonMistakes: [
    "Encrypted or damaged PDFs may fail to load.",
    "Merging does not compress images inside the PDF.",
    "Check page order before sharing the merged document.",
  ],
  relatedGuides: [privacyWorkflow],
  faqs: [
    {
      question: "Are PDFs uploaded during merge?",
      answer:
        "No. The merge workflow runs in your browser.",
    },
    {
      question: "Can this merge password-protected PDFs?",
      answer:
        "Password-protected or encrypted PDFs may not load in this browser workflow.",
    },
  ],
});

const pdfSplitContent = localToolContent({
  h1: "Local PDF Split",
  intro:
    "Extract selected page ranges from a PDF into a new browser-generated document without uploading the file.",
  examples: [
    {
      title: "Extract a page range",
      input: "pages 1-3, 8",
      output: "document-split.pdf",
      explanation:
        "Splitting locally helps share only the pages you need while keeping the source file on your device.",
    },
  ],
  useCases: [
    "Extract invoice or form pages from a larger PDF.",
    "Share a subset of documentation.",
    "Remove unnecessary pages before sending a file.",
  ],
  howItWorks: [
    "The source PDF is loaded in the browser.",
    "Selected page ranges are copied into a new PDF.",
    "The output PDF is generated locally.",
  ],
  commonMistakes: [
    "Page ranges are usually one-based, not zero-based.",
    "Splitting does not redact content on included pages.",
    "Encrypted or malformed PDFs may fail to load.",
  ],
  relatedGuides: [privacyWorkflow],
  faqs: [
    {
      question: "Is the PDF uploaded?",
      answer:
        "No. Splitting runs locally in your browser.",
    },
    {
      question: "Can I redact pages with this?",
      answer:
        "No. It extracts page ranges. Redaction needs a dedicated tool and careful verification.",
    },
  ],
});

const pdfCompressContent = localToolContent({
  h1: "Local PDF Compress",
  intro:
    "Optimize a PDF with a local lossless rewrite and document metadata cleanup, while preserving embedded images.",
  examples: [
    {
      title: "Rewrite a PDF locally",
      input: "large-export.pdf",
      output: "large-export-optimized.pdf",
      explanation:
        "A lossless rewrite can clean structure and metadata, but it does not recompress embedded images.",
    },
  ],
  useCases: [
    "Try a safe local rewrite before uploading a PDF elsewhere.",
    "Clean document metadata during a browser-local export.",
    "Optimize PDFs generated by simple browser workflows.",
  ],
  howItWorks: [
    "The PDF is loaded by the browser.",
    "Pages are saved into a fresh PDF structure.",
    "Embedded images are preserved rather than recompressed.",
  ],
  commonMistakes: [
    "Lossless PDF rewrite may not reduce files dominated by large images.",
    "Compression is not the same as redaction.",
    "Always review output before deleting the original file.",
  ],
  relatedGuides: [privacyWorkflow],
  faqs: [
    {
      question: "Will this always make a PDF smaller?",
      answer:
        "No. It is a lossless rewrite and metadata cleanup, not aggressive image recompression.",
    },
    {
      question: "Is the PDF uploaded?",
      answer:
        "No. Processing happens locally in your browser.",
    },
  ],
});

const headersCheckerContent = serverAssistedContent({
  h1: "Public HTTP Headers Checker",
  intro:
    "Inspect status and response headers for public HTTP and HTTPS URLs when debugging caching, content type, redirects, and deployment behavior.",
  examples: [
    {
      title: "Inspect a public response",
      input: "https://example.com",
      output: "Status, final URL, and response headers",
      explanation:
        "Response headers reveal caching, content type, redirects, security policy, and server/CDN behavior.",
    },
  ],
  useCases: [
    "Check whether a CDN or proxy is sending the expected headers.",
    "Debug content type, cache-control, or redirect responses.",
    "Capture header evidence for a deployment ticket.",
  ],
  howItWorks: [
    "The API fetches the public URL from the server side.",
    "Private, local, reserved, and credentialed URLs are blocked.",
    "The response status and headers are returned for inspection.",
  ],
  commonMistakes: [
    "Browser devtools can hide intermediate redirect headers unless preserved.",
    "Header names are case-insensitive.",
    "A public checker should not be used for private intranet URLs.",
  ],
  relatedGuides: [
    {
      title: "Audit security headers before launch",
      href: "/guides/audit-security-headers-before-launch",
      description:
        "Use response headers as the starting point for browser hardening checks.",
    },
  ],
  faqs: [
    {
      question: "Why does this need an API request?",
      answer:
        "Browsers cannot reliably fetch arbitrary public headers because of CORS and networking restrictions.",
    },
    {
      question: "Can I check private URLs?",
      answer:
        "No. The checker is designed for public HTTP and HTTPS URLs only.",
    },
  ],
});

const contentByToolId: Record<string, ToolSeoContent> = {
  "json-formatter": jsonFormatterContent,
  "json-minifier": jsonMinifierContent,
  "yaml-to-json": yamlToJsonContent,
  "jwt-decoder": jwtDecoderContent,
  base64: base64Content,
  "html-escape": htmlEscapeContent,
  "uuid-generator": uuidGeneratorContent,
  "hash-generator": hashGeneratorContent,
  "password-generator": passwordGeneratorContent,
  "timestamp-converter": timestampConverterContent,
  "url-encoder": urlEncoderContent,
  "color-converter": colorConverterContent,
  "csv-to-markdown": csvToMarkdownContent,
  "url-parser": urlParserContent,
  "curl-translator": curlTranslatorContent,
  "text-diff": textDiffContent,
  "text-statistics": textStatisticsContent,
  "slug-generator": slugGeneratorContent,
  "case-converter": caseConverterContent,
  "utm-builder": utmBuilderContent,
  "meta-tag-generator": metaTagGeneratorContent,
  "opengraph-preview": openGraphPreviewContent,
  "robots-generator": robotsGeneratorContent,
  "image-metadata-stripper": imageMetadataStripperContent,
  "image-resize": imageResizeContent,
  "svg-to-image": svgToImageContent,
  "pdf-merge": pdfMergeContent,
  "pdf-split": pdfSplitContent,
  "pdf-compress": pdfCompressContent,
  "headers-checker": headersCheckerContent,
  "security-headers-auditor": securityHeadersContent,
  "redirect-chain-checker": redirectChainContent,
};

export function getToolSeoContent(toolId: string) {
  return contentByToolId[toolId];
}
